import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, apiErrorMessage } from "../api/client";
import type { CampaignHistory } from "../api/types";
import { takeAsked } from "../lib/askedFiles";
import { dateAndTime } from "../lib/format";
import { DownloadJobFile, signedFiles } from "./DownloadJobFile";
import { Spinner } from "./Spinner";

/* A campaign's newest file, from the engine.
 *
 * It replaces ExportBand, which kept the job id in the card or the page that
 * asked for the file. On My campaigns an export with "Mark as calling" moved
 * the card from Ready to call to Calling, the new card had no job id, and the
 * Download went with the old one (the October 23 training call). Leaving the
 * page lost it the same way. The newest export row of api.campaign_history
 * is the engine's fact, so a stage move, a reload, another person and another
 * machine all read the same line (engine migration 293,
 * docs/plans/campaign-export-file.md there).
 *
 * The engine moves the campaign to Calling when the file is made, where this
 * page used to. When the file the line shows is made while the page is open,
 * the campaign and its lists are read again so the stage shows, and the file
 * downloads by itself when this tab asked for it. */

export const EXPORT_FILES = ["orum_export", "lead_sheet", "crm_export"];
export const THIN_FILE = ["thin_export"];

export type FileRow = Pick<CampaignHistory, "at" | "kind" | "what" | "by_name" | "job_id" | "has_file" | "failed" | "note">
  & { file: string | null };

const EXPORT_KINDS = ["export", "export_running", "export_failed"];

/** The campaign's newest file of these kinds, polled while it is being
 *  made. downloaded is true once this tab's file downloaded by itself, and
 *  fetchError says why it did not. */
export function useNewestFile(token: string, campaign: string, files: string[]) {
  const queryClient = useQueryClient();
  const running = useRef<string | null>(null);
  const [downloaded, setDownloaded] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["campaign-file", campaign, files.join(",")],
    enabled: Boolean(campaign),
    refetchInterval: (qq) => ((qq.state.data as FileRow | null | undefined)?.kind === "export_running" ? 3_000 : false),
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("campaign_history")
        .select("at,kind,what,by_name,job_id,has_file,failed,note,file")
        .eq("campaign", campaign).in("kind", EXPORT_KINDS).in("file", files)
        .order("at", { ascending: false }).limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? [])[0] ?? null) as FileRow | null;
    },
  });
  const row = q.data;
  const jobId = row?.job_id ?? null;
  const kind = row?.kind ?? null;

  useEffect(() => {
    if (!jobId) return;
    if (kind === "export_running") {
      running.current = jobId;
      setDownloaded(false);
      setFetchError(null);
      return;
    }
    // Made, or failed, while this page watched it run: the engine may have
    // moved the campaign to Calling, and the lists count the file.
    if (running.current === jobId) {
      running.current = null;
      for (const key of [["campaign", campaign], ["campaign-list"], ["campaign-history", campaign],
                         ["campaigns"], ["my-campaigns"]]) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    }
    if (kind === "export" && row?.has_file && takeAsked(jobId)) {
      signedFiles(token, jobId)
        .then((got) => {
          const first = got[0];
          if (!first) throw new Error("The file was made and the bucket has none.");
          window.location.assign(first.url);
          setDownloaded(true);
        })
        .catch((e: unknown) => setFetchError(e instanceof Error ? e.message : String(e)));
    }
  }, [jobId, kind, row?.has_file, campaign, token, queryClient]);

  return { q, row, downloaded, fetchError };
}

/** The line for a campaign's newest Orum list, Lead Sheet or CRM CSV: being
 *  made, made with its Download, past the bucket's 30 days, or failed with
 *  the engine's reason. Nothing before the first file. */
export function CampaignFile({ token, campaign }: { token: string; campaign: string }) {
  const { q, row, downloaded, fetchError } = useNewestFile(token, campaign, EXPORT_FILES);
  if (q.isError) {
    return <p className="refusal cp-band" role="alert">The newest file did not load. {(q.error as Error).message}</p>;
  }
  if (!row) return null;
  if (row.kind === "export_running") {
    return <div className="cp-band" role="status"><Spinner />{row.what}.</div>;
  }
  if (row.failed) {
    return <div className="cp-band cp-band-failed" role="alert">{row.what}. {row.note ?? ""}</div>;
  }
  return (
    <div className="cp-band" role="status">
      {row.has_file && row.job_id ? <DownloadJobFile token={token} jobId={row.job_id} /> : null}
      <span>{row.what}, {dateAndTime(row.at)}{row.by_name ? <>, {row.by_name}</> : null}.</span>
      {row.note ? <span className="muted">{row.note}</span> : null}
      {downloaded ? <span className="muted">Downloaded.</span> : null}
      {fetchError ? <span className="refusal">{fetchError}</span> : null}
    </div>
  );
}
